import { useState } from 'react'
import { FileDown, Mail, MessageCircle, X, CheckCircle, XCircle, Pencil } from 'lucide-react'
import PdfService from '../../services/PdfService'
import EmailSendService from '../../services/EmailSendService'
import WhatsAppLinkService from '../../services/WhatsAppLinkService'
import usePermissions from '../../hooks/usePermissions'

const fmtNum = (n, decimals = 2) =>
  parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—'
const currSym = (c) => c === 'USD' ? '$' : '₹'

const StatusBadge = ({ status }) => {
  if (status === 'completed') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
      <CheckCircle size={11} /> Completed
    </span>
  )
  if (status === 'cancelled') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      <XCircle size={11} /> Cancelled
    </span>
  )
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">{status}</span>
  )
}

const buildStoneCategoryTotals = (stonesArr) => {
  if (!Array.isArray(stonesArr) || stonesArr.length === 0) return {}
  const totals = {}
  stonesArr.forEach(s => {
    const code = s.stone_code || s.code || 'Unknown'
    const unit = (s.unit || 'CARAT').toUpperCase()
    const weight = parseFloat(s.weight) || 0
    const rate = parseFloat(s.sale_price || s.rate) || 0
    const cost = weight * rate
    let rawWeight = weight
    if (unit === 'CARAT' || unit === 'CT') rawWeight = weight * 0.2
    else if (unit === 'NUMBER' || unit === 'NOS' || unit === 'PCS') rawWeight = 0
    if (totals[code]) { totals[code].cost += cost; totals[code].rawWeight += rawWeight }
    else totals[code] = { unit, cost, rawWeight, category: s.stone_name || code }
  })
  return totals
}

/**
 * BillDetailModal — full invoice detail popup.
 * Props:
 *   bill       — bill object from /api/billings
 *   onClose    — called to close the modal
 *   onCancel   — optional; if provided shows "Cancel Bill" button
 */
const BillDetailModal = ({ bill: initialBill, onClose, onCancel }) => {
  const [bill, setBill] = useState(initialBill)
  const [pdfDownloading, setPdfDownloading] = useState(false)
  const [emailSending, setEmailSending] = useState(false)
  const [whatsappSending, setWhatsappSending] = useState(false)
  const [editingEmail, setEditingEmail] = useState(false)
  const [emailDraft, setEmailDraft] = useState('')
  const [savingEmail, setSavingEmail] = useState(false)
  const { hasPermission } = usePermissions()
  const canEditEmail = hasPermission('indian-billing', 'edit') || hasPermission('us-billing', 'edit')

  if (!bill) return null

  const stones = (() => {
    if (!bill.stones) return []
    if (Array.isArray(bill.stones)) return bill.stones
    try { return JSON.parse(bill.stones) } catch { return [] }
  })()

  const billItems = Array.isArray(bill.items) ? bill.items : []
  const isMultiItem = billItems.length > 1

  const isUSD = bill.currency === 'USD'
  const sym = currSym(bill.currency)
  const nw = parseFloat(bill.net_weight) || 0
  const gr = parseFloat(bill.gross_weight) || 0
  const goldVal = parseFloat(bill.gold_value) || 0
  const wastage = parseFloat(bill.other_charges) || 0
  const making = parseFloat(bill.making_charges) || 0
  const stoneTotal = parseFloat(bill.stone_total) || 0
  const discount = parseFloat(bill.discount_amount) || 0
  const taxAmt = parseFloat(bill.customs_amount) || 0
  const taxPct = parseFloat(bill.customs_percent) || 0
  const grandTotal = parseFloat(bill.grand_total) || 0
  const baseTotal = parseFloat(bill.base_total) || 0

  const totalGoldAmt = goldVal + wastage + making
  const makingPerGram = nw > 0 ? (making / nw) : 0
  const certCharges = baseTotal > 0 ? Math.max(0, baseTotal - totalGoldAmt - stoneTotal) : 0
  const grandTotalINR = isUSD ? baseTotal + taxAmt : grandTotal

  // billing_items amounts are stored in INR. For a USD bill, divide by the rate
  // implied by the stored totals so line rows read in the same currency as the
  // Grand Total. Falls back to the raw (INR) value for INR bills or if unknown.
  const multiGrandINR = billItems.reduce((s, it) => s + (parseFloat(it.item_total) || 0), 0)
  const billExchangeRate = isUSD && grandTotal > 0 ? multiGrandINR / grandTotal : 0
  const lineAmountInBillCcy = (inr) => {
    const n = parseFloat(inr) || 0
    return isUSD && billExchangeRate > 0 ? n / billExchangeRate : n
  }

  // Shared by PDF download and email send — both take the same payload shape.
  // Bill number always comes from bill.bill_number as already stored — never
  // regenerated or reformatted here, so an old bill keeps its original
  // number (whatever format it was created under) when resent.
  const buildInvoiceData = () => {
    const stoneCategoryTotals = buildStoneCategoryTotals(stones)
    const diamondCarats = stones
      .filter(s => (s.category || '').toLowerCase() === 'diamond')
      .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0)
    const colorStoneCarats = stones
      .filter(s => (s.category || '').toLowerCase() !== 'diamond'
        && ((s.unit || '').toLowerCase().includes('carat') || (s.unit || '').toLowerCase() === 'ct'))
      .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0)
    const goldPrice = nw > 0 ? goldVal / nw : 0
    const isUSDCurr = bill.currency === 'USD'
    const dollarRate = isUSDCurr && grandTotal > 0 ? grandTotalINR / grandTotal : 0
    return {
      currency: bill.currency,
      type: 'Invoice',
      companyName: 'GDS Jewellers',
      companyTagline: 'Hallmarked Certified Jewellery',
      date: bill.created_at,
      estimateNumber: bill.bill_number,
      billNumber: bill.bill_number,
      customerName: bill.customer_name,
      customerPhone: bill.customer_phone,
      customerEmail: bill.customer_email,
      customerCity: bill.customer_city,
      customerState: bill.customer_state,
      itemCode: bill.item_code,
      purity: bill.gold_purity,
      goldPrice: goldPrice.toFixed(2),
      grossWeight: bill.gross_weight,
      netWeight: bill.net_weight,
      makingCharge: nw > 0 ? (making / nw).toFixed(2) : '0',
      totalGoldAmount: totalGoldAmt.toFixed(2),
      stoneCategoryTotals,
      diamondCarats,
      colorStoneCarats: Math.max(0, colorStoneCarats),
      stoneTotal: stoneTotal.toFixed(2),
      discount: discount.toFixed(2),
      dollarRate,
      ...(isUSDCurr
        ? {
          customsDutyPercent: taxPct,
          taxAmount: taxAmt.toFixed(2),
          grandTotalINR: baseTotal > 0 ? baseTotal.toFixed(2) : grandTotal.toFixed(2),
          grandTotalUSD: grandTotal.toFixed(2),
          grandTotal: baseTotal > 0 ? baseTotal.toFixed(2) : grandTotal.toFixed(2),
        }
        : {
          taxRate: taxPct,
          taxAmount: taxAmt.toFixed(2),
          grandTotal: grandTotal.toFixed(2),
          grandTotalINR: grandTotal.toFixed(2),
        }
      ),
      notes: bill.notes || '',
    }
  }

  // Multi-item invoice payload — mirrors buildInvoiceData() but carries an
  // items[] array (one entry per billing_items line) instead of a single item.
  // Every line amount is INR; for a USD bill the exchange rate is derived from
  // the stored INR/USD totals, exactly as the single-item path does.
  const buildMultiItemInvoiceData = () => {
    const lineSubtotal = (it) =>
      (parseFloat(it.item_total) || 0) - (parseFloat(it.tax_amount) || 0)
    const baseTotalINR = baseTotal > 0
      ? baseTotal
      : billItems.reduce((sum, it) => sum + lineSubtotal(it), 0)
    const customsINR = taxAmt > 0
      ? taxAmt
      : billItems.reduce((sum, it) => sum + (parseFloat(it.tax_amount) || 0), 0)
    const grandINR = baseTotalINR + customsINR
    const exchangeRate = isUSD && grandTotal > 0 ? grandINR / grandTotal : 0
    return {
      currency: bill.currency,
      type: 'Invoice',
      companyName: 'GDS Jewellers',
      date: bill.created_at,
      billNumber: bill.bill_number,
      estimateNumber: bill.bill_number,
      customerName: bill.customer_name,
      customerPhone: bill.customer_phone,
      customerEmail: bill.customer_email,
      customerCity: bill.customer_city,
      customerState: bill.customer_state,
      exchangeRate,
      baseTotalINR: baseTotalINR.toFixed(2),
      customsINR: customsINR.toFixed(2),
      customsPercent: taxPct || null,
      grandTotalINR: grandINR.toFixed(2),
      // grandTotal carries the INR figure even for a USD bill, matching the
      // single-item buildInvoiceData() contract the email route relies on.
      grandTotal: grandINR.toFixed(2),
      grandTotalUSD: isUSD ? grandTotal.toFixed(2) : undefined,
      items: billItems.map(it => ({
        itemCode: it.item_code || '',
        purity: it.purity || '',
        grossWeight: it.gross_weight,
        netWeight: it.net_weight,
        stoneWeight: it.stone_weight,
        stones: Array.isArray(it.stones) ? it.stones : [],
        amountINR: (parseFloat(it.item_total) || 0).toFixed(2),
      })),
      notes: bill.notes || '',
    }
  }

  const handleDownloadPDF = async () => {
    setPdfDownloading(true)
    try {
      if (isMultiItem) {
        await PdfService.downloadMultiItemInvoicePDF(buildMultiItemInvoiceData(), bill.currency)
      } else {
        await PdfService.downloadInvoicePDF(buildInvoiceData(), bill.currency)
      }
    } catch (err) {
      alert('Failed to download PDF: ' + err.message)
    } finally {
      setPdfDownloading(false)
    }
  }

  const handleSendEmail = async () => {
    setEmailSending(true)
    try {
      const payload = isMultiItem ? buildMultiItemInvoiceData() : buildInvoiceData()
      await EmailSendService.sendEmail(payload, bill.customer_email)
    } finally {
      setEmailSending(false)
    }
  }

  const handleStartEditEmail = () => {
    setEmailDraft(bill.customer_email || '')
    setEditingEmail(true)
  }

  const handleSaveEmail = async () => {
    const trimmed = emailDraft.trim()
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      alert('Please enter a valid email address.')
      return
    }
    setSavingEmail(true)
    try {
      const res = await fetch(`/api/billings/${bill.id}/email`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ customer_email: trimmed }),
      })
      const data = await res.json()
      if (!res.ok || !data.success) {
        alert(data.error || 'Failed to update email')
        return
      }
      setBill(prev => ({ ...prev, customer_email: trimmed }))
      setEditingEmail(false)
    } catch {
      alert('Network error while updating email')
    } finally {
      setSavingEmail(false)
    }
  }

  const handleSendWhatsApp = async () => {
    setWhatsappSending(true)
    try {
      if (isMultiItem) {
        await WhatsAppLinkService.shareInvoice({
          customerName: bill.customer_name || 'Customer',
          phoneNumber: bill.customer_phone,
          date: bill.created_at,
          billNumber: bill.bill_number,
          currency: bill.currency,
          grandTotal: grandTotal.toFixed(2),
          companyName: 'GDS Jewellers',
          companyAddress: '15520 Pawnee St, Overland Park, KS - 66224',
          items: billItems.map(it => ({
            itemCode: it.item_code || '—',
            purity: it.purity ? `${it.purity}K` : '',
            netWeight: parseFloat(it.net_weight || 0).toFixed(3),
            amount: lineAmountInBillCcy(it.item_total).toFixed(2),
          })),
        }, bill.customer_phone)
        return
      }
      await WhatsAppLinkService.shareInvoice({
        customerName: bill.customer_name || 'Customer',
        phoneNumber: bill.customer_phone,
        date: bill.created_at,
        itemCode: bill.item_code,
        purity: bill.gold_purity,
        grossWeight: bill.gross_weight,
        netWeight: bill.net_weight,
        goldRate: nw > 0 ? (goldVal / nw).toFixed(2) : 0,
        wastageAmount: wastage.toFixed(2),
        makingCharge: nw > 0 ? (making / nw).toFixed(2) : 0,
        makingAmount: making.toFixed(2),
        goldAmount: totalGoldAmt.toFixed(2),
        stoneAmount: stoneTotal.toFixed(2),
        discount: discount.toFixed(2),
        grandTotal: grandTotal.toFixed(2),
        billNumber: bill.bill_number,
        currency: bill.currency,
        companyName: 'GDS Jewellers',
        companyAddress: '15520 Pawnee St, Overland Park, KS - 66224',
      }, bill.customer_phone)
    } finally {
      setWhatsappSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
        <div className="rounded-t-xl" style={{ background: '#1a1a2e' }}>
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-3">
              <img src="/gds-logo.png" alt="GDS" className="h-12 w-auto object-contain"
                onError={e => { e.target.style.display = 'none' }} />
              <div>
                <div className="text-amber-400 font-bold text-lg leading-tight">GDS Jewellers</div>
                <div className="text-gray-400 text-xs tracking-widest uppercase">Fine Jewelry &amp; Gemstones</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* ══ INFO PILLS ══════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-3 gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200">
          {[
            { label: 'DATE', value: fmtDate(bill.created_at) },
            { label: 'BILL #', value: bill.bill_number },
            { label: 'CUSTOMER', value: bill.customer_name || '—' },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg px-3 py-2">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">{label}</div>
              <div className="text-sm font-bold text-gray-800 mt-0.5 truncate">{value}</div>
            </div>
          ))}
        </div>

        <div className="p-5 space-y-4">

          {/* ══ CUSTOMER DETAILS ════════════════════════════════════════════════ */}
          {(bill.customer_phone || bill.customer_email || bill.customer_city || canEditEmail) && (
            <div className="grid grid-cols-3 gap-2 text-sm">
              {bill.customer_phone && (
                <div><span className="text-xs text-gray-400 block">Phone</span>
                  <span className="font-medium text-gray-700">{bill.customer_phone}</span></div>
              )}
              {(bill.customer_email || canEditEmail) && (
                <div className="col-span-2">
                  <span className="text-xs text-gray-400 block">Email</span>
                  {editingEmail ? (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <input
                        type="email"
                        value={emailDraft}
                        onChange={(e) => setEmailDraft(e.target.value)}
                        placeholder="customer@example.com"
                        className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded"
                        autoFocus
                      />
                      <button
                        onClick={handleSaveEmail}
                        disabled={savingEmail}
                        className="px-2 py-1 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {savingEmail ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingEmail(false)}
                        disabled={savingEmail}
                        className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span className="font-medium text-gray-700 inline-flex items-center gap-1.5">
                      {bill.customer_email || <span className="italic text-gray-400 font-normal">No email on file</span>}
                      {canEditEmail && (
                        <button onClick={handleStartEditEmail} title="Edit email" className="text-gray-400 hover:text-blue-600">
                          <Pencil size={12} />
                        </button>
                      )}
                    </span>
                  )}
                </div>
              )}
              {(bill.customer_city || bill.customer_state) && (
                <div><span className="text-xs text-gray-400 block">City</span>
                  <span className="font-medium text-gray-700">
                    {[bill.customer_city, bill.customer_state].filter(Boolean).join(', ')}
                  </span></div>
              )}
            </div>
          )}

          {/* ══ LINE ITEMS (multi-item bill) ════════════════════════════════════ */}
          {isMultiItem && (
            <div>
              <div className="text-xs font-bold text-white uppercase tracking-widest px-3 py-1.5 rounded-t-lg"
                style={{ background: '#1a1a2e' }}>
                ITEMS ({billItems.length})
              </div>
              <div className="overflow-x-auto border border-t-0 border-gray-200 rounded-b-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-blue-600">
                    <tr>
                      {['#', 'Item Code', 'Purity', 'Net Wt', 'Amount'].map((h, i) => (
                        <th key={h}
                          className={`px-3 py-2 text-xs font-bold text-white ${i === 4 ? 'text-right' : 'text-left'}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {billItems.map((it, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                        <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                        <td className="px-3 py-2 font-semibold text-gray-800">{it.item_code || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{it.purity ? `${it.purity}K` : '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{parseFloat(it.net_weight || 0).toFixed(3)} g</td>
                        <td className="px-3 py-2 font-bold text-blue-700 text-right">
                          {sym}{fmtNum(lineAmountInBillCcy(it.item_total))}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50 border-t-2 border-blue-200">
                      <td colSpan={4} className="px-3 py-2 text-sm font-bold text-blue-800">Grand Total</td>
                      <td className="px-3 py-2 text-sm font-bold text-blue-800 text-right">
                        {sym}{fmtNum(grandTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ══ JEWELRY DETAILS (single-item bill) ══════════════════════════════ */}
          {!isMultiItem && (
          <>
          <div>
            <div className="text-xs font-bold text-white uppercase tracking-widest px-3 py-1.5 rounded-t-lg"
              style={{ background: '#1a1a2e' }}>
              JEWELRY DETAILS
            </div>
            <div className="grid grid-cols-4 bg-slate-50 border border-t-0 border-gray-200 rounded-b-lg overflow-hidden">
              {[
                { label: 'Item Code', value: bill.item_code || '—' },
                { label: 'Purity', value: bill.gold_purity ? `${bill.gold_purity}K` : '—' },
                { label: 'Gross Wt', value: gr > 0 ? `${gr.toFixed(3)} g` : '—' },
                { label: 'Net Wt', value: nw > 0 ? `${nw.toFixed(3)} g` : '—' },
              ].map(({ label, value }, i) => (
                <div key={label} className={`px-3 py-3 ${i < 3 ? 'border-r border-gray-200' : ''}`}>
                  <div className="text-xs text-gray-400">{label}</div>
                  <div className="text-sm font-bold text-gray-800 mt-1">{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ══ GOLD CALCULATION ════════════════════════════════════════════════ */}
          <div>
            <div className="text-xs font-bold text-white uppercase tracking-widest px-3 py-1.5 rounded-t-lg"
              style={{ background: '#1a1a2e' }}>
              GOLD CALCULATION
            </div>
            <div className="bg-amber-50 border border-t-0 border-amber-200 rounded-b-lg px-4 py-3 flex justify-between items-center">
              <div>
                <div className="text-xs text-amber-700">Making Charge / gram</div>
                <div className="text-sm font-bold text-gray-800 mt-0.5">
                  {makingPerGram > 0 ? `₹${fmtNum(makingPerGram, 2)} / g` : '—'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-amber-700">Gold Value</div>
                <div className="text-sm font-semibold text-gray-700">₹{fmtNum(goldVal)}</div>
                {wastage > 0 && (
                  <div className="text-xs text-gray-500">+ Wastage ₹{fmtNum(wastage)}</div>
                )}
                {making > 0 && (
                  <div className="text-xs text-gray-500">+ Making ₹{fmtNum(making)}</div>
                )}
                <div className="text-sm font-bold text-amber-700 border-t border-amber-300 mt-1 pt-1">
                  Total Gold  ₹{fmtNum(totalGoldAmt)}
                </div>
              </div>
            </div>
          </div>

          {/* ══ STONE DETAILS ═══════════════════════════════════════════════════ */}
          {stones.length > 0 && (
            <div>
              <div className="text-xs font-bold text-white uppercase tracking-widest px-3 py-1.5 rounded-t-lg"
                style={{ background: '#1a1a2e' }}>
                STONE DETAILS
              </div>
              <div className="overflow-x-auto border border-t-0 border-gray-200 rounded-b-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-blue-600">
                    <tr>
                      {['Code', 'Unit', 'Weight', 'Price'].map((h, i) => (
                        <th key={h}
                          className={`px-3 py-2 text-xs font-bold text-white ${i === 3 ? 'text-right' : 'text-left'}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stones.map((s, i) => {
                      const unit = (s.unit || 'g').toUpperCase()
                      const rawWt = parseFloat(s.weight || 0)
                      const isPcs = unit === 'NOS' || unit === 'PCS' || unit === 'PIECES' || unit === 'EACH' || unit === 'NUMBER'
                      const rate = parseFloat(s.sale_price || s.rate || 0)
                      // rate is per the stone's own unit (per carat / per piece / per gram),
                      // matching how the bill's stone_total was computed
                      const cost = rawWt * rate
                      const uLabel = (unit === 'CARAT' || unit === 'CARATS' || unit === 'CT') ? 'Ct'
                        : isPcs ? 'Pcs' : 'g'
                      return (
                        <tr key={i} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                          <td className="px-3 py-2 font-semibold text-gray-800">
                            {s.stone_code || s.code || s.stone_name || s.name || '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-600 text-center">{uLabel}</td>
                          <td className="px-3 py-2 text-gray-600 text-center">{isPcs && Number.isInteger(rawWt) ? rawWt : rawWt.toFixed(3)}</td>
                          <td className="px-3 py-2 font-bold text-blue-700 text-right">
                            ₹{fmtNum(cost)}
                          </td>
                        </tr>
                      )
                    })}
                    <tr className="bg-blue-50 border-t-2 border-blue-200">
                      <td colSpan={3} className="px-3 py-2 text-sm font-bold text-blue-800">Stone Total</td>
                      <td className="px-3 py-2 text-sm font-bold text-blue-800 text-right">
                        ₹{fmtNum(stoneTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </>
          )}

          {/* ══ TOTALS SUMMARY ══════════════════════════════════════════════════ */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="divide-y divide-gray-100">
              <div className="flex justify-between px-4 py-2.5 bg-white">
                <span className="text-sm font-semibold text-gray-600">Gold Amount</span>
                <span className="text-sm font-bold text-gray-800">₹{fmtNum(totalGoldAmt)}</span>
              </div>
              {stoneTotal > 0 && (
                <div className="flex justify-between px-4 py-2.5 bg-white">
                  <span className="text-sm font-semibold text-gray-600">Stone Amount</span>
                  <span className="text-sm font-bold text-gray-800">₹{fmtNum(stoneTotal)}</span>
                </div>
              )}
              {certCharges > 0.5 && (
                <div className="flex justify-between px-4 py-2.5 bg-white">
                  <span className="text-sm font-semibold text-gray-600">Certification</span>
                  <span className="text-sm font-bold text-gray-800">₹{fmtNum(certCharges)}</span>
                </div>
              )}
              {discount > 0 && (
                <div className="flex justify-between px-4 py-2.5 bg-green-50">
                  <span className="text-sm font-semibold text-green-700">Discount</span>
                  <span className="text-sm font-bold text-green-700">−{sym}{fmtNum(discount)}</span>
                </div>
              )}
              {taxAmt > 0 && (
                <div className="flex justify-between px-4 py-2.5 bg-orange-50">
                  <span className="text-sm font-semibold text-gray-600">
                    {isUSD ? `Customs (${taxPct}%)` : `GST (${taxPct}%)`}
                  </span>
                  <span className="text-sm font-bold text-gray-800">
                    {isUSD ? `₹${fmtNum(taxAmt)}` : `${sym}${fmtNum(taxAmt)}`}
                  </span>
                </div>
              )}
            </div>

            {/* Grand Total banner */}
            <div className="flex items-center justify-between px-4 py-3.5" style={{ background: '#1a1a2e' }}>
              <div>
                <div className="text-gray-300 text-xs uppercase tracking-widest">
                  {isUSD ? 'Total INR' : 'Grand Total'}
                </div>
                <div className="text-amber-400 text-xl font-extrabold mt-0.5">
                  {grandTotalINR > 0 ? `₹${fmtNum(grandTotalINR)}` : '—'}
                </div>
              </div>
              {isUSD && (
                <div className="text-right">
                  <div className="text-gray-300 text-xs uppercase tracking-widest">Total USD</div>
                  <div className="text-amber-400 text-xl font-extrabold mt-0.5">
                    ${fmtNum(grandTotal)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ══ STATUS / NOTES ══════════════════════════════════════════════════ */}
          {bill.estimate_number && (
            <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              📋 Converted from estimate <strong>{bill.estimate_number}</strong>
            </div>
          )}
          {bill.status === 'cancelled' && bill.cancellation_reason && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <div className="text-xs font-bold text-red-600 uppercase tracking-wide mb-1">Cancellation Reason</div>
              <div className="text-sm text-red-700">{bill.cancellation_reason}</div>
            </div>
          )}

        </div>

        {/* ══ FOOTER ══════════════════════════════════════════════════════════ */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <span className="text-xs text-gray-400">
            Created by <strong>{bill.created_by_name || '—'}</strong>
            <span className="ml-2"><StatusBadge status={bill.status} /></span>
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={pdfDownloading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown size={14} />
              {pdfDownloading ? 'Generating…' : 'Download PDF'}
            </button>
            <button
              onClick={handleSendEmail}
              disabled={emailSending || !bill.customer_email}
              title={!bill.customer_email ? 'No customer email on file for this bill' : 'Resend invoice by email'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-slate-600 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail size={14} />
              {emailSending ? 'Sending…' : 'Email'}
            </button>
            <button
              onClick={handleSendWhatsApp}
              disabled={whatsappSending || !bill.customer_phone}
              title={!bill.customer_phone ? 'No customer phone on file for this bill' : 'Resend invoice by WhatsApp'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageCircle size={14} />
              {whatsappSending ? 'Opening…' : 'WhatsApp'}
            </button>
            {onCancel && bill.status === 'completed' && (
              <button onClick={() => onCancel(bill)}
                className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
                Cancel Bill
              </button>
            )}
            <button onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
              Close
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}

export default BillDetailModal
